// Claude: New component — the administrator-only user-management page requested directly by the
// Claude: user ("registering users, allocating or changing roles, and deleting users... a batch
// Claude: upload from csv file... an option to download an (empty) csv template"). Backend:
// Claude: /v1/admin-users/ (see admin_list_users/admin_create_user/admin_change_user_role/
// Claude: admin_delete_user/admin_users_csv_template/admin_bulk_upload_users in views.py).
// Claude: Role gating happens server-side (every endpoint is IsAdministrator-only) — this route is
// Claude: reachable by anyone logged in client-side, same pattern as /userroles, /review-queue,
// Claude: /duplicate-cleanup etc. already use; a non-administrator just gets a 403 on first load.
import React, { useState, useEffect, useContext } from "react";
import { baseUrl, getValidAccessToken } from "../shared";
import Logo from "../logo512.png";
import Navbar from "../navbar";
import Navbaranonuser from "../navbaranonuser";
import { AuthContext } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../i18n";

const ROLE_OPTIONS = [
  { code: "r_user", labelKey: "r_user" },
  { code: "editor", labelKey: "editor" },
  { code: "approver", labelKey: "approver" },
  { code: "administrator", labelKey: "administrator" },
];

const EMPTY_REGISTER_FORM = { username: "", email: "", first_name: "", last_name: "", role: "r_user" };

const UserAdmin = () => {
  const { auth } = useContext(AuthContext);
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  // Claude: per-row role-select draft, keyed by user id — the old UserRoleManager.js kept a
  // Claude: single shared `newGroup` value for every row's dropdown, so clicking "Change" on any
  // Claude: row actually applied whichever dropdown had been touched last, anywhere in the table.
  const [roleDrafts, setRoleDrafts] = useState({});
  const [registerForm, setRegisterForm] = useState(EMPTY_REGISTER_FORM);
  // Claude: the newly created account's one-time temporary password — shown once, inline, rather
  // Claude: than via alert() (see _generate_temporary_password's docstring in views.py: it's never
  // Claude: stored anywhere except the account's own hashed password, so this on-page notice is the
  // Claude: only chance the administrator gets to read and relay it).
  const [createdUserNotice, setCreatedUserNotice] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const fetchUsers = async () => {
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + "v1/admin-users/", {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`HTTP status ${response.status}`);
      }
      const data = await response.json();
      setUsers(data.users);
      setRoleDrafts(Object.fromEntries(data.users.map((u) => [u.id, u.role])));
    } catch (err) {
      console.error("Error:", err);
      // Claude: alpha-tester feedback — "the error message is displayed... but there is no link to
      // Claude: the login page." Same pattern UserRoleManager.js already uses.
      setError(
        <>
          Error fetching users — please{' '}
          <Link to="/login" className="text-blue-500 underline hover:text-blue-700">log in</Link> again.
        </>
      );
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + "v1/admin-users/create/", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(registerForm),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${data.error}`);
        return;
      }
      setCreatedUserNotice(data);
      setRegisterForm(EMPTY_REGISTER_FORM);
      fetchUsers();
    } catch (err) {
      alert(t("generic_error"));
    }
  };

  const handleRoleChange = async (userId) => {
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + `v1/admin-users/${userId}/role/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: roleDrafts[userId] }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${data.error}`);
        return;
      }
      fetchUsers();
    } catch (err) {
      alert(t("generic_error"));
    }
  };

  const handleDelete = async (targetUser) => {
    if (!window.confirm(t("delete_user_confirm", { username: targetUser.username }))) {
      return;
    }
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + `v1/admin-users/${targetUser.id}/`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${data.error}`);
        return;
      }
      fetchUsers();
    } catch (err) {
      alert(t("generic_error"));
    }
  };

  // Claude: a plain <a href="..."> can't carry the Authorization header this endpoint requires
  // Claude: (it's IsAdministrator-gated, same as every other admin-users endpoint), so the file has
  // Claude: to be fetched and handed to the browser as a Blob instead of navigated to directly.
  const handleDownloadTemplate = async () => {
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + "v1/admin-users/csv-template/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        alert(t("could_not_download_template"));
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "user_upload_template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t("generic_error"));
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      return;
    }
    setIsUploading(true);
    setUploadResult(null);
    const token = await getValidAccessToken();
    const formData = new FormData();
    formData.append("file", csvFile);
    try {
      const response = await fetch(baseUrl + "v1/admin-users/bulk-upload/", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${data.error}`);
        setIsUploading(false);
        return;
      }
      setUploadResult(data);
      setCsvFile(null);
      setIsUploading(false);
      fetchUsers();
    } catch (err) {
      alert(t("generic_error"));
      setIsUploading(false);
    }
  };

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {auth.userName === null && <Navbaranonuser />}
      {auth.userName != null && <Navbar />}

      <div className="flex flex-col justify-center items-center p-2">
        <h3 className="text-4xl font-bold mb-6">
          <Link to={"/"}>{t("user_admin")}</Link>
        </h3>

        {/* Register a new user */}
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-lg p-6 mb-6">
          <h4 className="text-xl font-semibold mb-4">{t("register")}</h4>
          {createdUserNotice && (
            <div className="bg-cyan-50 border border-cyan-700 text-cyan-900 rounded-lg p-3 mb-4">
              <p>{t("user_created", { username: createdUserNotice.username })}</p>
              <p>
                <strong>{t("temporary_password")}:</strong>{" "}
                <span className="font-mono">{createdUserNotice.temporary_password}</span>
              </p>
              <p className="text-sm">{t("temporary_password_notice")}</p>
              <button
                type="button"
                onClick={() => setCreatedUserNotice(null)}
                className="mt-2 bg-gray-700 text-white py-1 px-3 rounded-md text-sm hover:bg-gray-800"
              >
                {t("back")}
              </button>
            </div>
          )}
          <form onSubmit={handleRegisterSubmit}>
            <div className="mb-4">
              <label htmlFor="admin-new-username" className="block text-gray-700">
                <strong>{t("username")}:</strong>
              </label>
              <input
                id="admin-new-username"
                type="text"
                value={registerForm.username}
                onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                className="border p-2 w-full"
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="admin-new-email" className="block text-gray-700">
                <strong>{t("emailaddress")}:</strong>
              </label>
              <input
                id="admin-new-email"
                type="email"
                value={registerForm.email}
                onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                className="border p-2 w-full"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="admin-new-first-name" className="block text-gray-700">
                <strong>{t("firstname")}:</strong>
              </label>
              <input
                id="admin-new-first-name"
                type="text"
                value={registerForm.first_name}
                onChange={(e) => setRegisterForm({ ...registerForm, first_name: e.target.value })}
                className="border p-2 w-full"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="admin-new-last-name" className="block text-gray-700">
                <strong>{t("surname")}:</strong>
              </label>
              <input
                id="admin-new-last-name"
                type="text"
                value={registerForm.last_name}
                onChange={(e) => setRegisterForm({ ...registerForm, last_name: e.target.value })}
                className="border p-2 w-full"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="admin-new-role" className="block text-gray-700">
                <strong>{t("role")}:</strong>
              </label>
              <select
                id="admin-new-role"
                value={registerForm.role}
                onChange={(e) => setRegisterForm({ ...registerForm, role: e.target.value })}
                className="w-full p-2 border rounded-lg"
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>{t(option.labelKey)}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="bg-cyan-700 text-white py-2 px-4 rounded-lg hover:bg-cyan-800"
            >
              {t("register")}
            </button>
          </form>
        </div>

        {/* Bulk upload from CSV */}
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-lg p-6 mb-6">
          <h4 className="text-xl font-semibold mb-4">{t("bulk_upload_users")}</h4>
          <p className="mb-4 text-gray-700">{t("bulk_upload_help")}</p>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800 mb-4"
          >
            {t("download_csv_template")}
          </button>
          <div className="mb-4">
            <input
              type="file"
              accept=".csv"
              aria-label={t("choose_csv_file")}
              onChange={(e) => setCsvFile(e.target.files[0] || null)}
              className="border p-2 w-full"
            />
          </div>
          <button
            type="button"
            onClick={handleCsvUpload}
            disabled={!csvFile || isUploading}
            className="bg-cyan-700 text-white py-2 px-4 rounded-lg hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? t("uploading") : t("upload_csv")}
          </button>

          {uploadResult && (
            <div className="mt-4">
              <h5 className="font-semibold">{t("csv_upload_results")}</h5>
              <p>{t("rows_created", { count: uploadResult.created.length })}</p>
              {uploadResult.created.length > 0 && (
                <table className="border-separate [border-spacing:0.5rem]">
                  <thead>
                    <tr>
                      <th>{t("username")}</th>
                      <th>{t("role")}</th>
                      <th>{t("temporary_password")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.created.map((row) => (
                      <tr key={row.username}>
                        <td>{row.username}</td>
                        <td>{t(row.role)}</td>
                        <td className="font-mono">{row.temporary_password}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {uploadResult.errors.length > 0 && (
                <>
                  <p className="mt-2">{t("rows_skipped", { count: uploadResult.errors.length })}</p>
                  <table className="border-separate [border-spacing:0.5rem]">
                    <thead>
                      <tr>
                        <th>{t("row_label")}</th>
                        <th>{t("username")}</th>
                        <th>{t("error_label")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.errors.map((row) => (
                        <tr key={row.row}>
                          <td>{row.row}</td>
                          <td>{row.username || "—"}</td>
                          <td>{row.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </div>

        {/* Existing users: change role / delete */}
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-lg p-6">
          <h4 className="text-xl font-semibold mb-4">{t("existing_users")}</h4>
          <table className="border-separate [border-spacing:0.8rem]">
            <thead>
              <tr>
                <th>{t("username")}</th>
                <th>{t("emailaddress")}</th>
                <th>{t("role")}</th>
                <th>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>{u.email}</td>
                  <td>
                    <select
                      value={roleDrafts[u.id] ?? u.role}
                      onChange={(e) => setRoleDrafts({ ...roleDrafts, [u.id]: e.target.value })}
                      className="w-full p-2 border rounded-lg"
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.code} value={option.code}>{t(option.labelKey)}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      onClick={() => handleRoleChange(u.id)}
                      disabled={(roleDrafts[u.id] ?? u.role) === u.role}
                      className="bg-cyan-700 text-white py-1 px-3 rounded-md text-sm hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("change_role")}
                    </button>
                    {" "}
                    <button
                      onClick={() => handleDelete(u)}
                      className="bg-rose-700 text-white py-1 px-3 rounded-md text-sm hover:bg-rose-800"
                    >
                      {t("delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex flex-col justify-center items-center p-4">
        <Link to={"/"}>
          <img src={Logo} alt="Logo" width="100" height="100" />
        </Link>
      </div>
    </div>
  );
};

export default UserAdmin;
